/**
 * Imperatively sets/updates SEO meta tags in <head>.
 * Uses unique data-managed-by attribute so we can replace/remove our tags
 * without touching tags added by other code or by the initial HTML.
 */
const MANAGED_ATTR = "data-managed-by";
const MANAGED_VAL = "sportly-seo";

function setOrCreateMeta(selector, attrs) {
  // First try to find a tag we've previously managed.
  let el = document.head.querySelector(selector + `[${MANAGED_ATTR}="${MANAGED_VAL}"]`);
  // If not found, take over an existing tag (e.g. one defined in index.html).
  if (!el) {
    el = document.head.querySelector(selector);
    if (el) el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") {
      el.removeAttribute(k);
    } else {
      el.setAttribute(k, v);
    }
  });
}

function setOrCreateLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][${MANAGED_ATTR}="${MANAGED_VAL}"]`);
  if (!el) {
    el = document.head.querySelector(`link[rel="${rel}"]`);
    if (el) el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo(seo) {
  if (!seo || typeof seo !== "object") return;

  if (seo.title) document.title = seo.title;

  if (seo.description) {
    setOrCreateMeta('meta[name="description"]', {
      name: "description",
      content: seo.description,
    });
  }
  if (seo.keywords) {
    setOrCreateMeta('meta[name="keywords"]', {
      name: "keywords",
      content: seo.keywords,
    });
  }

  // Open Graph
  if (seo.og_title || seo.title) {
    setOrCreateMeta('meta[property="og:title"]', {
      property: "og:title",
      content: seo.og_title || seo.title,
    });
  }
  if (seo.og_description || seo.description) {
    setOrCreateMeta('meta[property="og:description"]', {
      property: "og:description",
      content: seo.og_description || seo.description,
    });
  }
  if (seo.og_image) {
    setOrCreateMeta('meta[property="og:image"]', {
      property: "og:image",
      content: seo.og_image,
    });
  }
  if (seo.og_url) {
    setOrCreateMeta('meta[property="og:url"]', {
      property: "og:url",
      content: seo.og_url,
    });
  }
  setOrCreateMeta('meta[property="og:type"]', { property: "og:type", content: "website" });

  // Twitter
  if (seo.twitter_card) {
    setOrCreateMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: seo.twitter_card,
    });
  }
  if (seo.og_title || seo.title) {
    setOrCreateMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: seo.og_title || seo.title,
    });
  }
  if (seo.og_description || seo.description) {
    setOrCreateMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.og_description || seo.description,
    });
  }
  if (seo.og_image) {
    setOrCreateMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: seo.og_image,
    });
  }

  // Favicon
  if (seo.favicon) {
    setOrCreateLink("icon", seo.favicon);
  }
}
